import React, { useState, useEffect, useRef } from 'react';
import { BiBot, BiX, BiRefresh, BiChevronRight } from 'react-icons/bi';
import { BsSend } from 'react-icons/bs';

import axios from '../../../axios';

// ---------------------------------------------------------------------------
// Ask Trivvy — slide-over chat drawer for the User Chats overview.
//
// Hooks up to the AI Query backend (POST /ai-query/v1/ask, then
// POST /ai-query/v1/:requestId/confirm). The backend is human-in-the-loop by
// design: ask only ever produces a PENDING plan preview, and nothing executes
// until the admin explicitly confirms it in the plan card below.
// ---------------------------------------------------------------------------

const AI_QUERY_ASK = '/ai-query/v1/ask';
const aiQueryConfirmUrl = (requestId) => '/ai-query/v1/' + requestId + '/confirm';

const SUGGESTIONS = [
    'What new issues surfaced this week?',
    'Which hashtags are trending negative?',
    'Which participants need follow-up?',
];

const getErrorText = (err, fallback) => {
    var data = err && err.response && err.response.data;
    if (data && data.error) {
        return data.detail ? (data.error + ' ' + data.detail) : data.error;
    }
    if (err && err.message && !err.response) return 'Network error: ' + err.message;
    return fallback || 'Something went wrong. Please try again.';
};

const collectionLabel = (collection) => {
    if (collection === 'textifygroupmessages') return 'chat messages';
    if (collection === 'users') return 'users';
    return collection || 'records';
};

// Human-readable one-liner for the plan preview card. The raw spec stays
// available under "View query details".
const describePlan = (preview) => {
    if (!preview) return 'Planned query';
    var label = collectionLabel(preview.collection);
    var op = preview.operation;
    if (op === 'count') return 'Count ' + label;
    if (op === 'find') return 'Find ' + label + (preview.limit ? ' (up to ' + preview.limit + ')' : '');
    if (op === 'themes') {
        return 'Theme analysis across hashtags' +
            (preview.maxGroups ? ' (up to ' + preview.maxGroups + ' groups)' : '');
    }
    if (op === 'aggregate') {
        var first = preview.pipeline && preview.pipeline[0];
        if (first && first.$vectorSearch && first.$vectorSearch.queryText) {
            return 'Semantic search for "' + first.$vectorSearch.queryText + '"';
        }
        return 'Analyze ' + label;
    }
    return 'Query ' + label;
};

// Compact filter summary for the plan card (org scoping is injected by the
// backend guard, so it is stripped here to keep the preview readable).
const describeFilter = (preview) => {
    if (!preview) return '';
    var filter = preview.filter && typeof preview.filter === 'object'
        ? Object.assign({}, preview.filter)
        : null;
    if (filter) delete filter.organizationId;
    var hasFilter = filter && Object.keys(filter).length > 0;
    var hasPipeline = preview.pipeline && preview.pipeline.length > 0;
    if (!hasFilter && !hasPipeline) return 'Across all hashtags';
    var text = hasFilter ? JSON.stringify(filter) : (preview.pipeline.length + ' pipeline stages');
    if (text.length > 160) text = text.slice(0, 160).trim() + '…';
    return text;
};

let msgSeq = 0;
const nextId = () => 'atv-' + (++msgSeq) + '-' + Date.now();

const AskTrivvyDrawer = ({ open, onClose, user }) => {
    const firstName = (user && user.profile && user.profile.firstName) || '';
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    // requestId of the latest unanswered clarification question — the next
    // message the admin sends is its answer, so it goes out as followUpTo.
    const [pendingClarification, setPendingClarification] = useState(null);
    const bodyRef = useRef(null);
    const inputRef = useRef(null);

    // --- open/close behavior: focus, escape, backdrop scroll lock ---
    useEffect(() => {
        if (open) {
            document.body.classList.add('atv-lock');
            var t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 350);
            var onKey = (e) => { if (e.key === 'Escape') onClose(); };
            window.addEventListener('keydown', onKey);
            return () => {
                document.body.classList.remove('atv-lock');
                clearTimeout(t);
                window.removeEventListener('keydown', onKey);
            };
        }
        return undefined;
    }, [open, onClose]);

    // --- keep the thread pinned to the latest message ---
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, open]);

    const resetConversation = () => {
        if (busy) return;
        setMessages([]);
        setInput('');
        setPendingClarification(null);
    };

    const pushMessage = (msg) => setMessages((prev) => prev.concat([msg]));

    const replaceMessage = (id, msg) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? Object.assign({}, msg, { id }) : m)));

    // ------------------------------------------------------------------
    // Ask — POST /ai-query/v1/ask { question }
    // ------------------------------------------------------------------
    const sendQuestion = (text) => {
        var question = (text || '').trim();
        if (!question || busy) return;

        setBusy(true);
        setInput('');
        pushMessage({ id: nextId(), role: 'user', kind: 'text', text: question });
        var thinkingId = nextId();
        pushMessage({ id: thinkingId, role: 'assistant', kind: 'thinking' });

        var body = { question };
        if (pendingClarification) body.followUpTo = pendingClarification;

        axios.post(AI_QUERY_ASK, body)
            .then((res) => {
                var data = res.data || {};
                if (data.needsClarification) {
                    // The admin's next message answers this — chain it.
                    setPendingClarification(data.requestId || null);
                    replaceMessage(thinkingId, {
                        role: 'assistant', kind: 'clarification',
                        text: data.clarificationQuestion || 'Could you give me a bit more detail?',
                    });
                } else {
                    setPendingClarification(null);
                    replaceMessage(thinkingId, {
                        role: 'assistant', kind: 'plan',
                        requestId: data.requestId,
                        interpretation: data.interpretation,
                        queryPreview: data.queryPreview,
                        expiresAt: data.expiresAt,
                        planState: 'pending',
                    });
                }
            })
            .catch((err) => {
                replaceMessage(thinkingId, {
                    role: 'assistant', kind: 'error',
                    text: getErrorText(err, 'Could not plan that question.'),
                });
            })
            .finally(() => setBusy(false));
    };

    // ------------------------------------------------------------------
    // Confirm — POST /ai-query/v1/:requestId/confirm { confirmed: true }
    // ------------------------------------------------------------------
    const confirmPlan = (msg) => {
        if (busy || !msg || msg.planState !== 'pending') return;
        setBusy(true);
        replaceMessage(msg.id, Object.assign({}, msg, { planState: 'running' }));

        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: true })
            .then((res) => {
                var data = res.data || {};
                replaceMessage(msg.id, {
                    role: 'assistant', kind: 'results',
                    interpretation: data.interpretation || msg.interpretation,
                    resultKind: data.kind,
                    resultCount: data.resultCount,
                    summary: data.summary,
                    results: data.results,
                    groupThemes: data.groupThemes,
                });
            })
            .catch((err) => {
                var status = err && err.response && err.response.status;
                if (status === 409 || status === 410 || status === 404) {
                    // Terminal backend states (already handled / expired) —
                    // the plan card can no longer be confirmed.
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'stale',
                        planError: getErrorText(err, 'This plan can no longer be confirmed.'),
                    }));
                } else {
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'pending',
                        planError: getErrorText(err, 'Query failed to execute.'),
                    }));
                }
            })
            .finally(() => setBusy(false));
    };

    // ------------------------------------------------------------------
    // Discard — POST /ai-query/v1/:requestId/confirm { confirmed: false }
    // ------------------------------------------------------------------
    const discardPlan = (msg) => {
        if (busy || !msg || msg.planState !== 'pending') return;
        setBusy(true);
        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: false })
            .catch(() => { /* discard is best-effort; the plan simply expires */ })
            .finally(() => {
                replaceMessage(msg.id, {
                    role: 'assistant', kind: 'text',
                    text: 'Plan discarded — nothing was executed.',
                    muted: true,
                });
                setBusy(false);
            });
    };

    // ------------------------------------------------------------------
    // Revise — POST /ai-query/v1/:requestId/confirm { confirmed: false, correction }
    // The backend replans immediately and returns the new PENDING plan.
    // ------------------------------------------------------------------
    const revisePlan = (msg, correction) => {
        var text = (correction || '').trim();
        if (busy || !msg || !text) return;
        setBusy(true);
        replaceMessage(msg.id, Object.assign({}, msg, { planState: 'running', planError: null }));

        axios.post(aiQueryConfirmUrl(msg.requestId), { confirmed: false, correction: text })
            .then((res) => {
                var data = res.data || {};
                if (data.revised) {
                    replaceMessage(msg.id, {
                        role: 'assistant', kind: 'text',
                        text: 'Revised plan requested — showing the new plan below.',
                        muted: true,
                    });
                    pushMessage({
                        id: nextId(), role: 'assistant', kind: 'plan',
                        requestId: data.revised.requestId,
                        interpretation: data.revised.interpretation,
                        queryPreview: data.revised.queryPreview,
                        expiresAt: data.revised.expiresAt,
                        planState: 'pending',
                        supersedesNote: true,
                    });
                } else if (data.needsClarification) {
                    replaceMessage(msg.id, {
                        role: 'assistant', kind: 'clarification',
                        text: data.clarificationQuestion || 'Could you give me a bit more detail?',
                    });
                } else {
                    replaceMessage(msg.id, Object.assign({}, msg, {
                        planState: 'pending',
                        planError: (data.replanError) || 'Could not revise the plan.',
                    }));
                }
            })
            .catch((err) => {
                replaceMessage(msg.id, Object.assign({}, msg, {
                    planState: 'pending',
                    planError: getErrorText(err, 'Could not revise the plan.'),
                }));
            })
            .finally(() => setBusy(false));
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendQuestion(input);
        }
    };

    return (
        <React.Fragment>
            <div
                className={'atv-backdrop' + (open ? ' atv-open' : '')}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside className={'atv-drawer' + (open ? ' atv-open' : '')} aria-hidden={!open}>
                {/* Header */}
                <div className="atv-header">
                    <div className="atv-header-icon"><BiBot size={22} /></div>
                    <div className="atv-header-titles">
                        <div className="atv-title-row">
                            <span className="atv-title">Ask Trivvy</span>
                            <span className="atv-beta">BETA</span>
                        </div>
                        <div className="atv-subtitle">AI analysis of user-initiated chats across hashtags</div>
                    </div>
                    <div className="atv-header-actions">
                        <button className="atv-icon-btn" onClick={resetConversation} title="Start a new conversation" disabled={busy}>
                            <BiRefresh size={20} />
                        </button>
                        <button className="atv-icon-btn" onClick={onClose} title="Close">
                            <BiX size={22} />
                        </button>
                    </div>
                </div>

                {/* Thread */}
                <div className="atv-body" ref={bodyRef}>
                    {messages.length === 0 && (
                        <React.Fragment>
                            <div className="atv-greeting">
                                <div className="atv-greeting-title">
                                    <span className="atv-sparkle">✦</span>
                                    <span>Hi {firstName || 'there'} — I've read across your user chats.</span>
                                </div>
                                <div className="atv-greeting-sub">
                                    Ask me anything about your user-initiated chats, or start with one of these:
                                </div>
                            </div>
                            <div className="atv-suggestions">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} className="atv-suggestion" onClick={() => sendQuestion(s)} disabled={busy}>
                                        <span>{s}</span>
                                        <BiChevronRight size={20} className="atv-suggestion-chevron" />
                                    </button>
                                ))}
                            </div>
                        </React.Fragment>
                    )}

                    {messages.map((m) => (
                        <MessageRow key={m.id} msg={m} busy={busy}
                            onConfirm={() => confirmPlan(m)}
                            onDiscard={() => discardPlan(m)}
                            onRevise={(correction) => revisePlan(m, correction)} />
                    ))}
                </div>

                {/* Composer */}
                <div className="atv-footer">
                    <div className="atv-composer">
                        <input
                            ref={inputRef}
                            className="atv-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder="Ask about your chats..."
                            disabled={busy}
                            maxLength={1000}
                        />
                        <button
                            className={'atv-send' + ((!input.trim() || busy) ? ' atv-send-disabled' : '')}
                            onClick={() => sendQuestion(input)}
                            disabled={!input.trim() || busy}
                            title="Send"
                        >
                            <BsSend size={16} />
                        </button>
                    </div>
                    <div className="atv-disclaimer">
                        Ask Trivvy analyzes chat content across your hashtags. Responses are generated by AI and should be reviewed.
                    </div>
                </div>
            </aside>
        </React.Fragment>
    );
};

// ---------------------------------------------------------------------------
// One row of the thread.
// ---------------------------------------------------------------------------
const MessageRow = ({ msg, busy, onConfirm, onDiscard, onRevise }) => {
    if (msg.role === 'user') {
        return (
            <div className="atv-row atv-row-user">
                <div className="atv-bubble atv-bubble-user">{msg.text}</div>
            </div>
        );
    }
    if (msg.kind === 'thinking') {
        return (
            <div className="atv-row">
                <div className="atv-thinking"><span /><span /><span /></div>
            </div>
        );
    }
    if (msg.kind === 'error') {
        return (
            <div className="atv-row">
                <div className="atv-error">{msg.text}</div>
            </div>
        );
    }
    if (msg.kind === 'clarification') {
        return (
            <div className="atv-row">
                <div className="atv-clarification">
                    <div className="atv-clarification-label">I need a bit more detail</div>
                    <div>{msg.text}</div>
                </div>
            </div>
        );
    }
    if (msg.kind === 'plan') {
        return <PlanCard msg={msg} busy={busy} onConfirm={onConfirm} onDiscard={onDiscard} onRevise={onRevise} />;
    }
    if (msg.kind === 'results') {
        return <ResultsCard msg={msg} />;
    }
    // plain assistant text (discard / revision notes)
    return (
        <div className="atv-row">
            <div className={'atv-note' + (msg.muted ? ' atv-note-muted' : '')}>{msg.text}</div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Plan preview card — the human-in-the-loop gate. Nothing runs until Confirm.
// ---------------------------------------------------------------------------
const PlanCard = ({ msg, busy, onConfirm, onDiscard, onRevise }) => {
    const [showCorrection, setShowCorrection] = useState(false);
    const [correction, setCorrection] = useState('');
    const preview = msg.queryPreview || {};
    const state = msg.planState || 'pending';
    const interactive = state === 'pending' && !busy;
    const expiresLabel = msg.expiresAt
        ? 'Plan expires ' + new Date(msg.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : '';

    return (
        <div className="atv-row">
            <div className={'atv-plan' + (state === 'stale' ? ' atv-plan-stale' : '')}>
                {msg.supersedesNote && (
                    <div className="atv-plan-revised-flag">Revised plan</div>
                )}
                <div className="atv-plan-kicker">Planned query — review before running</div>
                {msg.interpretation && (
                    <div className="atv-plan-interpretation">{msg.interpretation}</div>
                )}
                <div className="atv-plan-what">
                    <span className="atv-plan-what-label">Will do:</span> {describePlan(preview)}
                </div>
                <div className="atv-plan-filter">{describeFilter(preview)}</div>
                <details className="atv-plan-details">
                    <summary>View query details</summary>
                    <pre>{JSON.stringify(preview, null, 2)}</pre>
                </details>
                {expiresLabel && <div className="atv-plan-expires">{expiresLabel}</div>}
                {msg.planError && <div className="atv-plan-error">{msg.planError}</div>}

                {state === 'stale' ? (
                    <div className="atv-plan-stale-note">This plan is no longer active. Ask again to get a fresh plan.</div>
                ) : state === 'running' ? (
                    <div className="atv-plan-running">Running query…</div>
                ) : (
                    <div className="atv-plan-actions">
                        <button className="atv-btn-primary" onClick={onConfirm} disabled={!interactive}>
                            Confirm &amp; run
                        </button>
                        <button className="atv-btn-ghost" onClick={onDiscard} disabled={!interactive}>
                            Discard
                        </button>
                    </div>
                )}

                {state === 'pending' && (
                    <div className="atv-correction">
                        {!showCorrection ? (
                            <button className="atv-correction-toggle" onClick={() => setShowCorrection(true)} disabled={busy}>
                                Suggest a correction
                            </button>
                        ) : (
                            <div className="atv-correction-row">
                                <input
                                    className="atv-correction-input"
                                    value={correction}
                                    onChange={(e) => setCorrection(e.target.value)}
                                    placeholder="e.g. only the last 7 days…"
                                    disabled={busy}
                                    maxLength={500}
                                />
                                <button
                                    className="atv-btn-ghost atv-btn-small"
                                    onClick={() => { onRevise(correction); setCorrection(''); setShowCorrection(false); }}
                                    disabled={!correction.trim() || busy}
                                >
                                    Revise
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Minimal markdown rendering for LLM-generated text (themes summaries).
// Dependency-free and built as React elements (no innerHTML), so untrusted
// text stays safe. Supports ### headings, "- " bullet lists, **bold**,
// and plain paragraphs.
// ---------------------------------------------------------------------------
const renderInlineMd = (text, keyPrefix) => {
    var parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        var m = /^\*\*([^*]+)\*\*$/.exec(p);
        return m
            ? <strong key={keyPrefix + '-' + i}>{m[1]}</strong>
            : <React.Fragment key={keyPrefix + '-' + i}>{p}</React.Fragment>;
    });
};

const renderMarkdown = (text) => {
    var lines = String(text || '').split('\n');
    var blocks = [];
    var listItems = [];
    const flushList = () => {
        if (listItems.length) {
            blocks.push(<ul key={'ul-' + blocks.length} className="atv-md-list">{listItems}</ul>);
            listItems = [];
        }
    };
    lines.forEach((raw, idx) => {
        var line = raw.trim();
        if (!line) { flushList(); return; }
        var h = /^#{1,3}\s*(.+)$/.exec(line);
        if (h) {
            flushList();
            blocks.push(<div key={'h-' + idx} className="atv-md-h">{renderInlineMd(h[1], 'h' + idx)}</div>);
            return;
        }
        var li = /^[-*]\s+(.+)$/.exec(line);
        if (li) {
            listItems.push(<li key={'li-' + idx}>{renderInlineMd(li[1], 'li' + idx)}</li>);
            return;
        }
        flushList();
        blocks.push(<p key={'p-' + idx} className="atv-md-p">{renderInlineMd(line, 'p' + idx)}</p>);
    });
    flushList();
    return blocks;
};

// ---------------------------------------------------------------------------
// Executed result card.
// ---------------------------------------------------------------------------
const ResultsCard = ({ msg }) => {
    var kindLabel = msg.resultKind === 'count' ? 'Count'
        : msg.resultKind === 'themes' ? 'Themes'
        : msg.resultKind === 'documents' ? 'Messages' : 'Result';
    var docs = Array.isArray(msg.results) ? msg.results : [];
    var groups = Array.isArray(msg.groupThemes) ? msg.groupThemes : [];

    return (
        <div className="atv-row">
            <div className="atv-results">
                <div className="atv-results-kicker">
                    <span className="atv-results-badge">{kindLabel}</span>
                    {typeof msg.resultCount === 'number' && (
                        <span className="atv-results-count">
                            {msg.resultCount} {msg.resultCount === 1 ? 'result' : 'results'}
                        </span>
                    )}
                </div>
                {msg.summary && <div className="atv-results-summary">{renderMarkdown(msg.summary)}</div>}

                {groups.length > 0 && (
                    <div className="atv-groups">
                        {groups.map((g, i) => (
                            <div key={(g && g.group) || i} className="atv-group">
                                <div className="atv-group-name">
                                    #{g && g.group ? String(g.group) : 'Group ' + (i + 1)}
                                    {typeof g.messageCount === 'number' && (
                                        <span className="atv-group-count"> · {g.messageCount} msgs</span>
                                    )}
                                </div>
                                {g && g.themes && <div className="atv-group-themes">{renderMarkdown(String(g.themes))}</div>}
                            </div>
                        ))}
                    </div>
                )}

                {docs.length > 0 && (
                    <details className="atv-plan-details">
                        <summary>View {Math.min(docs.length, 8)} of {docs.length} messages</summary>
                        <div className="atv-docs">
                            {docs.slice(0, 8).map((d, i) => (
                                <pre key={d._id || d.id || i} className="atv-doc">
                                    {formatDoc(d)}
                                </pre>
                            ))}
                        </div>
                    </details>
                )}
            </div>
        </div>
    );
};

// Compact one-doc rendering: prefer readable fields over a raw dump.
const formatDoc = (d) => {
    if (!d || typeof d !== 'object') return String(d);
    var lines = [];
    if (d.body) lines.push(String(d.body));
    var meta = [];
    if (d.currentGroupName) meta.push('#' + d.currentGroupName);
    if (d.createdAt) {
        try { meta.push(new Date(d.createdAt).toLocaleString()); } catch (e) { /* ignore */ }
    }
    if (d.textSentimentsData && d.textSentimentsData.sentiment) meta.push(String(d.textSentimentsData.sentiment).toLowerCase());
    if (meta.length > 0) lines.push(meta.join(' · '));
    if (lines.length === 0) {
        var raw = JSON.stringify(d);
        return raw.length > 300 ? raw.slice(0, 300) + '…' : raw;
    }
    return lines.join('\n');
};

export default AskTrivvyDrawer;
